/**
 * "We'll run your regatta" — the enquiry CTA.
 *
 * Distinct from `CreateRacePanel` on the home page, which is SELF-SERVE: a
 * sailor sends us tracks and gets a replay back. This one is the SERVICE:
 * we come to the event, put the cameras up, set the course in our software
 * and hand the fleet full replays. Different audience (an organising
 * authority, not a sailor), different action (an email to us, not an upload),
 * so it is a separate panel rather than more copy inside that one.
 *
 * It replaced the "Upload Tracks" banner on the regatta page. That banner
 * pointed at a flow that errored, and taking tracks from whoever is signed in
 * is not something we want for a scored regatta (Bir, 2026-07-31).
 *
 * Three shapes, one source of truth for the copy:
 *   panel   — a card in a right-hand rail, where it has the column to itself.
 *   banner  — full width, for the landing page.
 *   compact — the regatta page, beside the header.
 *
 * WHY `compact` EXISTS
 *
 * `panel` next to the regatta header ran ~200px against a ~110px header. In a
 * flex row the taller item sets the height, so the card pushed the standings
 * tabs down and left a dead band under the chips (Bir, 2026-07-31). It also
 * put a saturated blue button directly beside the page title, which outranked
 * the thing people came for.
 *
 * So `compact` drops the icon, the button and the repeated address — the link
 * is already a mailto — and lands near the header's own height. Same words,
 * quieter voice.
 *
 * `className` carries the caller's own spacing rather than baking a margin in,
 * because the sites sit in different stacks.
 */
const EMAIL = 'hello@regattareplay.com';
const MAILTO = `mailto:${EMAIL}?subject=${encodeURIComponent('Regatta coverage enquiry')}`;

/**
 * The address IS the label, in every shape.
 *
 * `mailto:` does nothing visible when the browser has no mail handler
 * registered — common on desktop Chrome with webmail — so a link reading
 * "Talk to us" leaves someone with a dead click and no way to reach us. The
 * rail panel printed the address underneath as a fallback; the regatta card
 * and the landing banner did not, and those are the two placements an
 * organising authority actually sees (Bir, 2026-07-31).
 *
 * Showing the address costs no height, works when the handler is missing, and
 * can be copied. The headline and pitch already do the inviting, so the action
 * does not also need to.
 */
const ACTION_LABEL = EMAIL;

const HEADLINE = 'Running a regatta?';
const PITCH =
  'We bring the cameras, set your course in our software, and hand every boat their own replay.';
/** The full pitch wraps to three lines in a 224px column, which is what made
 *  the card outgrow the regatta header. Same offer, fewer lines. */
const PITCH_SHORT = 'We bring the cameras and hand every boat their own replay.';

export function RunYourRegattaCTA({
  variant = 'panel',
  className = '',
}: {
  variant?: 'panel' | 'banner' | 'compact';
  className?: string;
}) {
  if (variant === 'compact') {
    return (
      <div
        className={`rounded-lg border border-viewer-line/25 bg-viewer-rise px-3.5 py-3 ${className}`}
      >
        <p className="text-[13px] font-semibold text-viewer-ink">{HEADLINE}</p>
        <p className="mt-1 text-[11.5px] leading-snug text-viewer-muted">{PITCH_SHORT}</p>
        {/* A text link, not a filled button: beside the regatta title, a solid
            blue block reads as the page's primary action, which it is not. */}
        <a
          href={MAILTO}
          className="mt-2 inline-block break-all text-[12px] font-semibold text-viewer-accent hover:underline"
        >
          {ACTION_LABEL}
        </a>
      </div>
    );
  }

  if (variant === 'banner') {
    return (
      <div className={`bg-viewer-rise border border-viewer-line/25 rounded-lg p-4 flex items-center justify-between gap-4 ${className}`}>
        <div className="flex items-start gap-3">
          <span
            aria-hidden
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg border border-viewer-accent/30 bg-viewer-accent/10 text-[14px] text-viewer-accent"
          >
            ◎
          </span>
          <div>
            <p className="text-sm font-medium text-viewer-ink">{HEADLINE}</p>
            <p className="text-sm text-viewer-muted">{PITCH}</p>
          </div>
        </div>
        {/* An <a>, not a Button-as-link: this leaves the app entirely. */}
        <a
          href={MAILTO}
          className="shrink-0 rounded-lg bg-brand-ink px-4 py-2.5 text-[13px] font-semibold text-white hover:bg-brand-dark"
        >
          {ACTION_LABEL}
        </a>
      </div>
    );
  }

  return (
    <div className={`rounded-xl border border-viewer-line/25 bg-gradient-to-b from-viewer-panel2 to-viewer-panel px-4 py-4 ${className}`}>
      <span
        aria-hidden
        className="flex h-8 w-8 items-center justify-center rounded-lg border border-viewer-accent/30 bg-viewer-accent/10 text-[14px] text-viewer-accent"
      >
        ◎
      </span>
      <p className="mt-3 font-viewer-display text-[15px] font-bold text-viewer-ink">{HEADLINE}</p>
      <p className="mt-1 text-[11.5px] leading-relaxed text-viewer-muted">{PITCH}</p>
      <a
        href={MAILTO}
        className="mt-3.5 block rounded-lg bg-brand-ink px-4 py-2.5 text-center text-[12.5px] font-semibold text-white hover:bg-brand-dark"
      >
        {ACTION_LABEL}
      </a>
      {/* The caption that used to repeat the address is gone — the button says
          it now, so printing it twice was noise. */}
    </div>
  );
}
